/**
 * DevToolsPanel - Dev-only floating panel for auto-completing lessons/quizzes
 *
 * Only rendered when import.meta.env.DEV is true.
 * Provides UI buttons to trigger auto-complete logic from devAutoComplete.ts
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import {
    autoCompleteLesson,
    autoCompleteModuleLessons,
    autoCompleteModuleEndQuiz,
    type LogLevel,
    type AutoCompleteCallbacks,
} from '@/lib/devAutoComplete'
import { useTranslation } from 'react-i18next'
import { Wrench, X, ChevronDown, ChevronUp, Play, Square, Loader2 } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export interface DevToolsPanelProps {
    selectedLessonId: number | null
    selectedContentType: string | null
    selectedModuleId: number | null
    resolvedContentId: number | null
    classProductId: number
    productId: number
    classId: number
    onRefresh: () => void
}

interface LogEntry {
    id: number
    message: string
    level: LogLevel
    timestamp: Date
}

// ============================================================================
// Component
// ============================================================================

export function DevToolsPanel({
    selectedLessonId,
    selectedContentType,
    selectedModuleId,
    resolvedContentId,
    classProductId,
    productId,
    classId,
    onRefresh,
}: DevToolsPanelProps) {
    const { t } = useTranslation('learner')
    const [isOpen, setIsOpen] = useState(false)
    const [isMinimized, setIsMinimized] = useState(false)
    const [isRunning, setIsRunning] = useState(false)
    const [logs, setLogs] = useState<LogEntry[]>([])
    const [progress, setProgress] = useState<{ current: number; total: number; label: string } | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)
    const logEndRef = useRef<HTMLDivElement>(null)
    const logIdRef = useRef(0)

    // Auto-scroll logs
    useEffect(() => {
        logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [logs])

    const addLog = useCallback((message: string, level: LogLevel) => {
        setLogs(prev => [...prev, { id: ++logIdRef.current, message, level, timestamp: new Date() }])
    }, [])

    const createCallbacks = useCallback((): AutoCompleteCallbacks => {
        const controller = new AbortController()
        abortControllerRef.current = controller

        return {
            onLog: addLog,
            onProgress: (current, total, label) => setProgress({ current, total, label }),
            onComplete: () => {
                setIsRunning(false)
                setProgress(null)
                onRefresh()
            },
            signal: controller.signal,
        }
    }, [addLog, onRefresh])

    const handleCancel = useCallback(() => {
        abortControllerRef.current?.abort()
        setIsRunning(false)
        setProgress(null)
        addLog(t('learning.devTools.cancelled'), 'warn')
    }, [addLog])

    const runAction = useCallback(async (action: (cb: AutoCompleteCallbacks) => Promise<void>) => {
        setIsRunning(true)
        setProgress(null)
        const cb = createCallbacks()
        try {
            await action(cb)
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                // Already handled in handleCancel
            } else {
                addLog(`${t('learning.devTools.error')}: ${err instanceof Error ? err.message : err}`, 'error')
            }
        } finally {
            setIsRunning(false)
            setProgress(null)
            onRefresh()
        }
    }, [createCallbacks, addLog, onRefresh])

    // =========================================================================
    // Action Handlers
    // =========================================================================

    const handleCompleteCurrentLesson = useCallback(() => {
        if (!selectedLessonId) return
        addLog(`=== 현재 레슨 자동 완료 (ID: ${selectedLessonId}) ===`, 'info')
        runAction(cb => autoCompleteLesson(selectedLessonId, { classProductId, productId, classId }, cb))
    }, [selectedLessonId, classProductId, productId, classId, runAction, addLog])

    const handleCompleteModuleLessons = useCallback(() => {
        if (!resolvedContentId || !selectedModuleId) return
        addLog(`=== 모듈 레슨 일괄 완료 (모듈 ID: ${selectedModuleId}) ===`, 'info')
        runAction(cb => autoCompleteModuleLessons(resolvedContentId, selectedModuleId, { classProductId, productId, classId }, cb))
    }, [resolvedContentId, selectedModuleId, classProductId, productId, classId, runAction, addLog])

    const handleCompleteModuleQuiz = useCallback(() => {
        if (!selectedModuleId) return
        addLog(`=== 모듈 마무리 퀴즈 완료 (ID: ${selectedModuleId}) ===`, 'info')
        runAction(cb => autoCompleteModuleEndQuiz(selectedModuleId, classProductId, cb))
    }, [selectedModuleId, classProductId, runAction, addLog])

    const handleCompleteModuleQuizLeaveLast = useCallback(() => {
        if (!selectedModuleId) return
        addLog(`=== 모듈 마무리 퀴즈 1문제 남기고 완료 (ID: ${selectedModuleId}) ===`, 'info')
        runAction(cb => autoCompleteModuleEndQuiz(selectedModuleId, classProductId, cb, { leaveLast: 1 }))
    }, [selectedModuleId, classProductId, runAction, addLog])

    // =========================================================================
    // Render: FAB Button (closed state)
    // =========================================================================

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed right-4 bottom-20 z-[9999] flex items-center justify-center w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg transition-colors"
                title="Dev Tools"
            >
                <Wrench className="w-5 h-5" />
            </button>
        )
    }

    // =========================================================================
    // Render: Panel (open state)
    // =========================================================================

    const progressPct = progress && progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0

    return (
        <div className="fixed right-4 bottom-4 z-[9999] w-96 bg-gray-900 text-gray-100 rounded-lg shadow-2xl border border-gray-700 flex flex-col max-h-[600px]">
            {/* Header */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-800 rounded-t-lg border-b border-gray-700">
                <div className="flex gap-2 items-center">
                    <Wrench className="w-4 h-4 text-orange-400" />
                    <span className="text-sm font-semibold text-orange-400">Dev Tools</span>
                </div>
                <div className="flex gap-1 items-center">
                    <button
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="p-1 rounded hover:bg-gray-700"
                    >
                        {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-1 rounded hover:bg-gray-700"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <>
                    {/* Action Buttons */}
                    <div className="p-3 space-y-2 border-b border-gray-700">
                        <button
                            onClick={handleCompleteCurrentLesson}
                            disabled={isRunning || !selectedLessonId}
                            className="flex gap-2 items-center px-3 py-2 w-full text-sm bg-blue-600 rounded transition-colors hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500"
                        >
                            <Play className="w-3.5 h-3.5" />
                            {t('learning.devTools.autoCompleteLesson')}
                            {selectedLessonId && <span className="ml-auto text-xs opacity-60">ID:{selectedLessonId}</span>}
                        </button>

                        <button
                            onClick={handleCompleteModuleLessons}
                            disabled={isRunning || !resolvedContentId || !selectedModuleId}
                            className="flex gap-2 items-center px-3 py-2 w-full text-sm bg-purple-600 rounded transition-colors hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500"
                        >
                            <Play className="w-3.5 h-3.5" />
                            {t('learning.devTools.autoCompleteModuleLessons')}
                            {selectedModuleId && <span className="ml-auto text-xs opacity-60">ID:{selectedModuleId}</span>}
                        </button>

                        <button
                            onClick={handleCompleteModuleQuiz}
                            disabled={isRunning || !selectedModuleId}
                            className="flex gap-2 items-center px-3 py-2 w-full text-sm bg-emerald-600 rounded transition-colors hover:bg-emerald-700 disabled:bg-gray-700 disabled:text-gray-500"
                        >
                            <Play className="w-3.5 h-3.5" />
                            {t('learning.devTools.autoCompleteModuleQuiz')}
                            {selectedModuleId && <span className="ml-auto text-xs opacity-60">ID:{selectedModuleId}</span>}
                        </button>

                        <button
                            onClick={handleCompleteModuleQuizLeaveLast}
                            disabled={isRunning || !selectedModuleId}
                            className="flex gap-2 items-center px-3 py-2 w-full text-sm bg-amber-600 rounded transition-colors hover:bg-amber-700 disabled:bg-gray-700 disabled:text-gray-500"
                        >
                            <Play className="w-3.5 h-3.5" />
                            {t('learning.devTools.autoCompleteQuizLeaveLast')}
                            {selectedModuleId && <span className="ml-auto text-xs opacity-60">ID:{selectedModuleId}</span>}
                        </button>

                        {isRunning && (
                            <button
                                onClick={handleCancel}
                                className="flex gap-2 justify-center items-center px-3 py-2 w-full text-sm bg-red-600 rounded transition-colors hover:bg-red-700"
                            >
                                <Square className="w-3.5 h-3.5" />
                                {t('learning.devTools.cancel')}
                            </button>
                        )}
                    </div>

                    {/* Progress Bar */}
                    {isRunning && progress && (
                        <div className="px-3 py-2 border-b border-gray-700">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-gray-400">{progress.label}</span>
                                <span className="text-xs text-gray-400">{progressPct}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-orange-500 rounded-full transition-all duration-300"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Running Indicator */}
                    {isRunning && !progress && (
                        <div className="flex gap-2 items-center px-3 py-2 border-b border-gray-700">
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />
                            <span className="text-xs text-gray-400">{t('learning.devTools.running')}</span>
                        </div>
                    )}

                    {/* Log Area */}
                    <div className="flex-1 overflow-y-auto min-h-[120px] max-h-[300px] p-2 font-mono text-xs space-y-0.5">
                        {logs.length === 0 && (
                            <div className="py-4 text-center text-gray-500">
                                {t('learning.devTools.clickToStart')}
                            </div>
                        )}
                        {logs.map(log => (
                            <div
                                key={log.id}
                                className={
                                    log.level === 'error' ? 'text-red-400' :
                                    log.level === 'warn' ? 'text-yellow-400' :
                                    log.level === 'success' ? 'text-green-400' :
                                    'text-gray-300'
                                }
                            >
                                {log.level === 'success' && '✓ '}
                                {log.level === 'error' && '✗ '}
                                {log.level === 'warn' && '⚠ '}
                                {log.message}
                            </div>
                        ))}
                        <div ref={logEndRef} />
                    </div>

                    {/* Footer: Clear Logs */}
                    {logs.length > 0 && (
                        <div className="px-3 py-1.5 border-t border-gray-700">
                            <button
                                onClick={() => setLogs([])}
                                className="text-xs text-gray-500 transition-colors hover:text-gray-300"
                            >
                                {t('learning.devTools.clearLogs')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
